import { faChevronDown, faTimes } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { ReactNode, useCallback, useMemo } from 'react';
import styled from 'styled-components';

import { styles } from '../../styles';
import { BaseSelect, BaseSelectProps } from '../baseSelect';
import { defaultRenderOption } from '../baseSelect/default';

import { DefaultDeselect } from './default';

const ChevronContainer = styled.div`
  ${styles.pl.md};
  ${styles.color.textLight};
`;

export function Select<Option extends { label?: ReactNode; value: any }>({
  value,
  placeholder,
  options,
  renderOption = defaultRenderOption,
  extractKey,
  deselectIcon = <FontAwesomeIcon style={{ marginLeft: 4 }} icon={faTimes} />,
  onDeselect,
  ...props
}: {
  placeholder?: ReactNode;
  onDeselect?: (opt: Option) => void;
  deselectIcon?: ReactNode;
  value:
    | Option
    | (Option extends { key: string | number } ? Option['key'] : never)
    | (Option extends { value: any } ? Option['value'] : never);
} & Omit<BaseSelectProps<Option>, 'renderContent' | 'isOptionSelected'>) {
  const selectedOpt = useMemo(() => {
    if (value == null) {
      return null;
    }
    return options?.find((opt) => {
      const o = opt as any;
      return o === value || o.key === value || o.value === value;
    });
  }, [value, options]);

  const renderContent = useCallback(
    ({ showSearchInput }: { showSearchInput?: boolean }) => {
      if (showSearchInput) {
        return null;
      }
      if (selectedOpt) {
        return renderOption(selectedOpt, { active: false, placeholder: true });
      }
      return <div style={{ flexGrow: 1 }} />;
    },
    [renderOption, selectedOpt]
  );

  const renderContentAfter = useCallback(() => {
    if (!selectedOpt || !onDeselect) {
      return (
        <ChevronContainer>
          <FontAwesomeIcon icon={faChevronDown} />
        </ChevronContainer>
      );
    }
    return (
      <DefaultDeselect
        style={{ marginLeft: 4 }}
        onClick={(e) => {
          e.stopPropagation();
          e.preventDefault();
          onDeselect(selectedOpt);
        }}
      >
        {deselectIcon}
      </DefaultDeselect>
    );
  }, [deselectIcon, onDeselect, selectedOpt]);

  const isOptionSelected = useCallback(
    (opt) => {
      return opt === selectedOpt;
    },
    [selectedOpt]
  );

  return (
    <BaseSelect
      options={options}
      renderContent={renderContent}
      isOptionSelected={isOptionSelected}
      extractKey={extractKey}
      renderContentAfter={renderContentAfter}
      searchPlaceholder={
        // @ts-ignore
        selectedOpt && typeof selectedOpt.label === 'string'
          ? // @ts-ignore
            selectedOpt.label
          : ''
      }
      {...props}
    />
  );
}
